import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { environment } from "@environments/environment";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { PermissionsResponse, Profile, User, UserResponse } from "../../interfaces/perfil.interface";
import { ApiResponse } from "../../interfaces/api.interface";
import { SessionKeys, StorageType } from "../../enums";
import { AuthenticationService } from "../../../pages/services/authentication.service";
import { StorageService } from "../storage/storage.service";

@Injectable({
  providedIn: 'root'
})
export class UserService implements OnDestroy {

  user: BehaviorSubject<User | undefined> = new BehaviorSubject<User | undefined>(undefined);

  private readonly http: HttpClient = inject(HttpClient);
  private readonly authService: AuthenticationService = inject(AuthenticationService);
  private readonly storage: StorageService = inject(StorageService);
  private readonly apiUrl: string = environment.baseURL;

  async init(): Promise<void> {

    let userStored: User | null = this.storage.getItem<User>(SessionKeys.USER, StorageType.SESSION);
    let profileStored: Profile | null = this.storage.getItem<Profile>(SessionKeys.PROFILE, StorageType.SESSION);
    if (userStored?.idUsuario && profileStored?.rol) {
      this.applyUser(userStored, profileStored);
      return;
    }
    await this.loadAndCacheUser();
  }

  private applyUser(user: User, profile: Profile) {
    this.authService.setAuthenticationInfo(profile);
    this.user.next(user);
  }

  async fetchUserInfo(): Promise<UserResponse> {
    const { data, error } = await firstValueFrom(this.http.get<ApiResponse<UserResponse>>(`${this.apiUrl}perfil`));
    if (error || !data) throw error;
    return data;
  }

  private async loadAndCacheUser() {
    try {
      let { rol, permisos = [], ...info } = await this.fetchUserInfo();
      permisos = (permisos as PermissionsResponse[]).map(p => p.permisos.nombre);
      const profile = { rol, permisos };

      this.storage.setItem(SessionKeys.USER, info, StorageType.SESSION);
      this.applyUser(info as User, profile);

    } catch (err) {
      console.error('Failed to fetch user:', err);
    }
  }

  reset(): void {
    this.user.next(undefined);
  }

  ngOnDestroy() {
    this.user.complete();
  }
}
